import React, { forwardRef, useImperativeHandle, useState, useRef, CSSProperties } from 'react';
import { FaExclamationCircle, FaExclamationTriangle } from 'react-icons/fa';
import InputMask from 'react-input-mask';


enum InputType {
    TEXT = 'text',
    NUMBER = 'number',
    TEL = 'tel',
    PASSWORD = 'password'
}

interface TextBoxProps {
    label: string;
    inputId: string;
    required?: boolean;
    errorMessage?: string;
    placeholder?: string;
    type?: InputType;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    value?: string;
    maxLength?: number;
    className?: string;
    mask?: string | null; // Mask parametresi eklendi
    style?: CSSProperties | null; // style prop'u ekleniyor
    description?: string | null; // Açıklama özelliği eklendi
}

export interface TextBoxHandle {
    validate: () => boolean;
    showError: () => void;
    hideError: () => void;
    getValue: () => string; // Adding getValue function to the handle
    setValue: (value: string) => void; // Adding setValue function to the handle
}

const TextBox = forwardRef<TextBoxHandle, TextBoxProps>(({
    label,
    inputId,
    required = false,
    errorMessage,
    placeholder,
    type = InputType.TEXT,
    onChange,
    value,
    className,
    mask,
    style = null, // style prop'u varsayılan olarak null
    description = null
}, ref) => {
    const [isValid, setIsValid] = useState(true);
    const [inputValue, setInputValue] = useState(value || '');
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
        validate: () => {
            const inputElement = document.getElementById(inputId) as HTMLInputElement;
            if (required && !inputElement.value) {
                setIsValid(false);
                return false;
            }
            setIsValid(true);
            return true;
        },
        showError: () => {
            setIsValid(false);
        },
        getValue: () => {
            const inputElement = document.getElementById(inputId) as HTMLInputElement;
            return inputElement.value;
        },
        setValue(value: string) {

            setInputValue(value);
            if (inputRef.current) {
                inputRef.current.value = value;
            }
        },
        hideError: () => {
            setIsValid(true);
        },
    }));

    // Default onChange handler
    // Default onChange handler
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
        if (onChange) {
            onChange(event);
        }
    };

    const handleType = type ?? InputType.TEXT;
    const handleValue = value ?? '';
    const handleInputId = inputId ?? 'input-' + Math.floor(Math.random() * 999999);

    return (

        <div className={className}>
            <div className={`form-group`}>
                <label className="form-label" htmlFor={inputId}>
                    {label}
                </label>

                {/* Eğer mask varsa InputMask kullan, yoksa standart input */}
                {mask ? (
                    <InputMask
                        mask={mask}
                        className={`form-control mb-0 ${!isValid ? 'is-invalid' : ''}`}
                        required={required}
                        placeholder={placeholder}
                        onChange={handleChange}
                        value={inputValue}
                        ref={inputRef as React.Ref<any>} // InputMask ref türüyle uyumlu hale getiriyoruz
                    />
                ) : (
                    <input
                        id={inputId}
                        type={handleType}
                        className={`form-control mb-0 ${!isValid ? 'is-invalid' : ''}`}
                        required={required}
                        placeholder={placeholder}
                        onChange={handleChange}
                        value={inputValue}
                        ref={inputRef}
                    />
                )}

                {!isValid && (
                    <div className={`bg-soft-danger danger-container ${!description ? 'danger-container-border' : ''}`}>
                        <div className="danger-icon">
                            <FaExclamationTriangle />
                        </div>
                        <div className="danger-text">
                            <span>
                                {errorMessage}
                            </span>
                        </div>
                    </div>
                )}

                {description && (
                    <div className="warning-container bg-soft-warning">
                        <div className="warning-icon">
                            <FaExclamationCircle />
                        </div>
                        <div className="warning-text">
                            <span>
                                {description}
                            </span>
                        </div>
                    </div>
                )}

            </div>
        </div>

    );
});


export { TextBox, InputType }; // InputType enumunu dışa aktarıyoruz

export default TextBox;
